import type { Device as ModelDevice, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { locateProxy } from '#~/agent/utils.js';
import type { Device } from '#~/device/index.js';
import { locateDevice, locateRoom } from '#~/room/index.js';
import {
  resolveDeviceAnalyticsCatalogContextFromModel,
  type DeviceAnalyticsCatalogContext,
  type DeviceAnalyticsCatalogModelLike
} from '#~/analytics/device-catalog.js';

// The catalog resolvers live in device-catalog.ts so emit sites that need only
// brand/model can reach them without inheriting this file's agent/room imports,
// which pull the UI graph. Re-exported here so existing consumers are unaffected.
export {
  resolveDeviceAnalyticsCatalogContext,
  resolveDeviceAnalyticsCatalogContextFromModel
} from '#~/analytics/device-catalog.js';
export type {
  DeviceAnalyticsCatalogContext,
  DeviceAnalyticsCatalogModelLike
} from '#~/analytics/device-catalog.js';

export type DeviceAnalyticsContext = DeviceAnalyticsCatalogContext & {
  device_id: string;
  agent_id?: string;
  studio_id?: string;
  is_embedded?: boolean;
  added_at?: string;
  uptime_seconds?: number;
};

type DeviceAnalyticsLike = Pick<ModelDevice, 'id'> &
  Readonly<{
    addedAt?: number;
    modelId?: Model['id'];
  }>;

type DeviceAnalyticsProxyLike = {
  embedded?: boolean;
  id?: string;
  roomId?: string;
};

type ResolveDeviceAnalyticsContextOptions = {
  includeUptime?: boolean;
  nowMs?: number;
};

type ResolveDeviceAnalyticsContextFromDeviceOptions = ResolveDeviceAnalyticsContextOptions & {
  proxy?: T_Maybe<DeviceAnalyticsProxyLike>;
  studioId?: string;
  // ENG-4456 — a caller that already resolved the model (e.g. by awaiting the
  // registry fetch it kicked off itself) supplies it here so brand/model labels
  // survive even when the shared registry store hasn't cached this model yet.
  model?: T_Maybe<DeviceAnalyticsCatalogModelLike>;
};

const toAnalyticsTimestamp = (
  addedAt: T_Maybe<number>,
  nowMs: number,
  includeUptime: boolean
): Pick<DeviceAnalyticsContext, 'added_at' | 'uptime_seconds'> => {
  if (!addedAt || addedAt <= 0) {
    return {};
  }

  const nowSeconds = Math.floor(nowMs / 1000);

  return {
    added_at: new Date(addedAt * 1000).toISOString(),
    ...(includeUptime ? { uptime_seconds: Math.max(0, Math.floor(nowSeconds - addedAt)) } : {})
  };
};

export const resolveDeviceAnalyticsContextFromDevice = (
  device: T_Maybe<DeviceAnalyticsLike>,
  options: ResolveDeviceAnalyticsContextFromDeviceOptions = {}
): T_Maybe<DeviceAnalyticsContext> => {
  if (!device) {
    return undefined;
  }

  const { includeUptime = true, model, nowMs = Date.now(), proxy, studioId } = options;
  const deviceProxy = proxy ?? locateProxy(device.id as Device['id']);
  const resolvedAgentId = deviceProxy?.id;
  const resolvedStudioId = studioId ?? deviceProxy?.roomId;

  return {
    device_id: device.id,
    // Per ENG-4456 — falls back to a caller-supplied resolved model (e.g. from an
    // awaited registry fetch) when the shared registry store hasn't cached this
    // model yet, same fallback the add path already relies on for this reason.
    ...resolveDeviceAnalyticsCatalogContextFromModel(model, device.modelId),
    ...(resolvedAgentId ? { agent_id: resolvedAgentId } : {}),
    ...(resolvedStudioId ? { studio_id: resolvedStudioId } : {}),
    ...(deviceProxy?.embedded !== undefined ? { is_embedded: deviceProxy.embedded } : {}),
    ...toAnalyticsTimestamp(device.addedAt, nowMs, includeUptime)
  };
};

export const resolveDeviceAnalyticsContext = (
  deviceId: Device['id'],
  options: ResolveDeviceAnalyticsContextOptions = {}
): T_Maybe<DeviceAnalyticsContext> => {
  const device = locateDevice(deviceId);

  if (!device) {
    return undefined;
  }

  const room = locateRoom(deviceId);

  return resolveDeviceAnalyticsContextFromDevice(device, {
    ...options,
    ...(room?.id ? { studioId: room.id } : {})
  });
};
